import {
  CaptureUpdateAction,
  useExcalidrawAPI,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import {
  isFrameLikeElement,
  isFreeDrawElement,
  isLinearElement,
} from "@excalidraw/element";
import { useEffect, useReducer, useRef, useState } from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import {
  animationPresets,
  supportsAnchorPick,
  supportsCustomPath,
} from "../animation/presets";
import { makeClip } from "../animation/clips";
import { beginPickAnchor } from "../animation/anchorPick";
import { beginDrawPath } from "../animation/pathDraw";
import {
  computeStaggerDelays,
  STAGGER_FROM_OPTIONS,
} from "../animation/stagger";
import {
  getElementAnimations,
  withElementAnimations,
} from "../animation/types";

import { useActiveClip } from "./ActiveClipContext";
import { ClipSettings } from "./ClipSettings";
import { FlipbookPanel } from "./FlipbookPanel";
import { TemplateGallery } from "./TemplateGallery";

import "./AnimationPanel.scss";

import type { ElementAnimation } from "../animation/types";

/** dataTransfer type for gallery cards — a custom MIME keeps the core app's
 * text/plain drop path (URL embeds) from ever seeing preset ids. */
const PRESET_DRAG_MIME = "application/x-excalidraw-animation-preset";

/** Survives the panel unmounting when another sidebar tab is picked — the
 * Flipbook mode's own Export button sends you to the Screens tab, and coming
 * back to Templates instead of the book you were editing reads as a bug. */
let lastMode: "templates" | "flipbook" = "templates";

export const AnimationPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const { activeClip, setActiveClip, setSettingsOpen } = useActiveClip();

  // useUIAppState only re-renders on appState; applying a preset and editing
  // the customize window mutate scene customData (element.animations), so
  // subscribe to scene changes and force a render — otherwise the gallery's
  // selected-preset highlight and the customize inputs go stale.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return excalidrawAPI.onChange(() => forceRender());
  }, [excalidrawAPI]);

  const elements = excalidrawAPI?.getSceneElements() ?? [];
  const selectedElements = elements.filter(
    (el) => appState.selectedElementIds[el.id] && !isFrameLikeElement(el),
  );

  // resolve which clip the gallery highlights and the settings window edits:
  // the active clip if it still points at a selected element with that index,
  // else the first selected element's first clip (if any)
  const resolveActive = (): {
    element: ExcalidrawElement;
    index: number;
    clip: ElementAnimation;
  } | null => {
    if (activeClip) {
      const el = selectedElements.find((e) => e.id === activeClip.elementId);
      const clips = el ? getElementAnimations(el) : [];
      if (el && clips[activeClip.index]) {
        return {
          element: el,
          index: activeClip.index,
          clip: clips[activeClip.index],
        };
      }
    }
    for (const el of selectedElements) {
      const clips = getElementAnimations(el);
      if (clips.length > 0) {
        return { element: el, index: 0, clip: clips[0] };
      }
    }
    return null;
  };
  const active = resolveActive();
  const selectedAnimation = active?.clip ?? null;

  // tracks whether the active clip's settings are expanded inline below the
  // "applied to selection" chips — independent of the floating canvas dock's
  // own settingsOpen, so opening one doesn't force the other open too
  const [sidebarClipOpen, setSidebarClipOpen] = useState(false);

  // Templates (per-element animation presets) vs Flipbook (the frame-by-frame
  // timeline) — both live under the same Animation tab
  const [mode, setModeState] = useState<"templates" | "flipbook">(lastMode);
  const setMode = (next: typeof mode) => {
    lastMode = next;
    setModeState(next);
  };
  // multi-element apply: delay ramp across the selection (0 = off)
  const [staggerEachMs, setStaggerEachMs] = useState(0);
  const [staggerFrom, setStaggerFrom] =
    useState<typeof STAGGER_FROM_OPTIONS[number]>("first");

  // mutateElement never records an undo entry on its own — capture explicitly
  // so clip edits don't get folded into the next unrelated undo step
  const captureUndo = () => {
    excalidrawAPI?.updateScene({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  /** Update the active clip in place within its element's list. */
  const updateActiveClip = (updates: Partial<ElementAnimation>) => {
    if (!active) {
      return;
    }
    const clips = getElementAnimations(active.element);
    const current = clips[active.index];
    // switching preset must NOT merge the old preset's params — stale keys
    // (e.g. followPath's rotate/snap) would leak into the new preset and can
    // collide with same-named params that mean something else
    const replaceParams = updates.preset && updates.preset !== current.preset;
    const next = clips.map((clip, i) =>
      i === active.index
        ? {
            ...current,
            ...updates,
            params: replaceParams
              ? { ...updates.params }
              : { ...current.params, ...updates.params },
          }
        : clip,
    );
    excalidrawAPI?.mutateElement(active.element, {
      customData: withElementAnimations(active.element, next),
    });
    captureUndo();
  };

  /** Remove the active clip from its element's list. */
  const removeActiveClip = () => {
    if (!active) {
      return;
    }
    const clips = getElementAnimations(active.element);
    excalidrawAPI?.mutateElement(active.element, {
      customData: withElementAnimations(
        active.element,
        clips.filter((_, i) => i !== active.index),
      ),
    });
    captureUndo();
    setActiveClip(null);
    setSidebarClipOpen(false);
  };

  /** Apply the preset as a new clip on the given elements — preserving the
   * followPath / morphToElement cross-element referencing. */
  const addPresetToElements = (
    presetId: string,
    elementsToAnimate: readonly ExcalidrawElement[],
  ) => {
    const preset = animationPresets[presetId];
    // referenced element (stored as pathElementId) — followPath: a selected
    // line/arrow/freedraw becomes the motion path; morphToElement: the
    // topmost selected element becomes the morph target
    const referencedElement =
      elementsToAnimate.length > 1
        ? presetId === "followPath" || presetId === "textOnPath"
          ? elementsToAnimate.find(
              (el) => isLinearElement(el) || isFreeDrawElement(el),
            )
          : presetId === "morphToElement"
          ? elementsToAnimate[elementsToAnimate.length - 1]
          : undefined
        : undefined;
    let targets = referencedElement
      ? elementsToAnimate.filter((el) => el !== referencedElement)
      : [...elementsToAnimate];
    if (presetId === "morphToElement") {
      // the morph renderer only draws these types (see drawElementOnCanvas
      // in renderElement.ts); other source types would tween morphProgress
      // with nothing rendering, so drop them here rather than silently no-op
      const morphSupportedTypes = new Set([
        "rectangle",
        "diamond",
        "ellipse",
        "iframe",
        "embeddable",
      ]);
      targets = targets.filter((el) => morphSupportedTypes.has(el.type));
    }
    // delay ramp across the selection — baked into each clip's delay so the
    // result is plain scene data (visible in the timeline, one undo)
    const staggerDelays =
      targets.length > 1 && staggerEachMs > 0
        ? computeStaggerDelays(targets, {
            eachMs: staggerEachMs,
            from: staggerFrom,
            // scene-derived seed keeps "random" stable across re-applies
            seed: targets.length * 31 + staggerEachMs,
          })
        : null;
    for (const el of targets) {
      const clip = makeClip(preset.id, {
        ...(referencedElement ? { pathElementId: referencedElement.id } : {}),
        ...(staggerDelays ? { delay: staggerDelays.get(el.id) ?? 0 } : {}),
      });
      excalidrawAPI?.mutateElement(el as ExcalidrawElement, {
        customData: withElementAnimations(el, [
          ...getElementAnimations(el),
          clip,
        ]),
      });
    }
    if (targets.length > 0) {
      captureUndo();
    }
    if (targets[0]) {
      setActiveClip({
        elementId: targets[0].id,
        index: getElementAnimations(targets[0]).length - 1,
      });
    }
    setSettingsOpen(true);
    // followPath applied without a line/arrow in the selection → switch to
    // the line tool so the user draws the motion path, then bind it
    if (presetId === "followPath" && !referencedElement && excalidrawAPI) {
      beginDrawPath(
        excalidrawAPI,
        targets.map((el) => ({
          elementId: el.id,
          index: getElementAnimations(el).length - 1,
        })),
      );
    }
  };

  /** Gallery clicks always ADD a clip to the selection — stacking, never
   * replacing. Changing an existing clip's preset lives in its settings. */
  const applyPreset = (presetId: string) => {
    addPresetToElements(presetId, selectedElements);
  };

  /** Dropping a gallery card on the canvas applies the preset to the element
   * under the cursor (topmost), falling back to the current selection. The
   * core app has no handler for this MIME, so the drop is claimed here with
   * document-level capture listeners. */
  const dropHandlerRef = useRef<(e: DragEvent) => void>(() => {});
  dropHandlerRef.current = (e: DragEvent) => {
    const presetId = e.dataTransfer?.getData(PRESET_DRAG_MIME);
    if (!presetId || !animationPresets[presetId] || !excalidrawAPI) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // a drop back onto the sidebar cancels the drag
    if ((e.target as Element | null)?.closest(".sidebar")) {
      return;
    }
    // useUIAppState strips scroll/zoom — read the full app state instead
    const { x, y } = viewportCoordsToSceneCoords(
      e,
      excalidrawAPI.getAppState(),
    );
    // topmost element under the drop point (last in paint order);
    // ponytail: axis-aligned bounds only, rotation ignored
    const sceneElements = excalidrawAPI.getSceneElements();
    let hit: ExcalidrawElement | undefined;
    for (let i = sceneElements.length - 1; i >= 0; i--) {
      const el = sceneElements[i];
      if (
        !isFrameLikeElement(el) &&
        x >= el.x &&
        x <= el.x + el.width &&
        y >= el.y &&
        y <= el.y + el.height
      ) {
        hit = el;
        break;
      }
    }
    const targets = hit ? [hit] : selectedElements;
    if (targets.length > 0) {
      // select the drop target so the animation bar & settings window (which
      // resolve the active clip from the selection) show the new clip
      excalidrawAPI.updateScene({
        appState: {
          selectedElementIds: Object.fromEntries(
            targets.map((el) => [el.id, true as const]),
          ),
        },
      });
      addPresetToElements(presetId, targets);
    }
  };
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes(PRESET_DRAG_MIME)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }
    };
    const onDrop = (e: DragEvent) => dropHandlerRef.current(e);
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", onDrop, true);
    return () => {
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", onDrop, true);
    };
  }, []);

  // rows of selected elements that already have at least one clip — shown as
  // a chip list right under the search box so applied animations are visible
  // without scrolling past the whole template grid first
  const appliedRows = selectedElements
    .map((el) => ({ el, clips: getElementAnimations(el) }))
    .filter((row) => row.clips.length > 0);

  const appliedSlot =
    appliedRows.length === 0 ? null : (
      <div className="applied-animations">
        <h4 className="applied-animations__title">Applied to selection</h4>
        {appliedRows.map(({ el, clips }) => (
          <div key={el.id} className="applied-animations__row">
            {appliedRows.length > 1 && (
              <span className="applied-animations__row-label" title={el.type}>
                {el.type}
              </span>
            )}
            <div className="applied-animations__chips">
              {clips.map((clip, index) => {
                const isActive =
                  active?.element.id === el.id && active.index === index;
                return (
                  <button
                    key={index}
                    className={`applied-animations__chip${
                      isActive && sidebarClipOpen
                        ? " applied-animations__chip--active"
                        : ""
                    }`}
                    onClick={() => {
                      setActiveClip({ elementId: el.id, index });
                      setSidebarClipOpen(!(isActive && sidebarClipOpen));
                    }}
                  >
                    {animationPresets[clip.preset]?.name ?? clip.preset}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {sidebarClipOpen && active && (
          <div className="applied-animations__settings">
            <ClipSettings
              clip={active.clip}
              element={active.element}
              elements={elements}
              appState={appState}
              onUpdate={updateActiveClip}
              onRemove={removeActiveClip}
              pathTools={
                active.clip.preset === "followPath" && excalidrawAPI
                  ? {
                      boundTo:
                        elements.find(
                          (el) => el.id === active.clip.pathElementId,
                        )?.type ?? null,
                      onDrawPath: () =>
                        beginDrawPath(excalidrawAPI, [
                          {
                            elementId: active.element.id,
                            index: active.index,
                          },
                        ]),
                    }
                  : undefined
              }
              onDrawMorphTarget={
                excalidrawAPI && supportsCustomPath(active.clip.preset)
                  ? () =>
                      beginDrawPath(
                        excalidrawAPI,
                        [{ elementId: active.element.id, index: active.index }],
                        "morphTarget",
                      )
                  : undefined
              }
              onPickAnchor={
                excalidrawAPI && supportsAnchorPick(active.clip.preset)
                  ? () =>
                      beginPickAnchor(excalidrawAPI, [
                        { elementId: active.element.id, index: active.index },
                      ])
                  : undefined
              }
            />
          </div>
        )}
      </div>
    );

  return (
    <div className="animation-panel">
      <div className="animation-panel__modes hig-segmented" role="tablist">
        {/* `.hig-segmented__item` picks the active segment up off
            aria-selected, so no separate --active class is needed */}
        <button
          role="tab"
          aria-selected={mode === "templates"}
          className="animation-panel__mode hig-segmented__item"
          onClick={() => setMode("templates")}
        >
          Templates
        </button>
        <button
          role="tab"
          aria-selected={mode === "flipbook"}
          className="animation-panel__mode hig-segmented__item"
          onClick={() => setMode("flipbook")}
        >
          Flipbook
        </button>
      </div>

      {mode === "flipbook" && <FlipbookPanel />}
      {/* unmounted rather than hidden while the flipbook shows: this panel
          re-renders on every scene change, and a hidden-but-mounted gallery
          would still reconcile ~140 cards on each one — mid-stroke. Mounting
          it back is cheap because PresetPreview builds a tile's timeline only
          once the tile is on screen. */}
      {mode === "templates" && (
        <section className="animation-panel__gallery-section">
          {selectedElements.length > 1 && (
            <div className="animation-panel__stagger">
              <label>
                Stagger (ms)
                <input
                  type="number"
                  min={0}
                  max={5000}
                  step={50}
                  value={staggerEachMs}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (Number.isFinite(value)) {
                      setStaggerEachMs(Math.max(0, Math.min(5000, value)));
                    }
                  }}
                  title="Delay added per element when applying a preset to the whole selection (0 = simultaneous)"
                />
              </label>
              {staggerEachMs > 0 && (
                <label>
                  From
                  <select
                    value={staggerFrom}
                    onChange={(e) =>
                      setStaggerFrom(
                        e.target.value as typeof STAGGER_FROM_OPTIONS[number],
                      )
                    }
                  >
                    {STAGGER_FROM_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          <TemplateGallery
            onDragStart={(presetId, e) => {
              e.dataTransfer!.effectAllowed = "copy";
              e.dataTransfer!.setData(PRESET_DRAG_MIME, presetId);
            }}
            onSelectPreset={(presetId) => {
              if (selectedElements.length > 0) {
                applyPreset(presetId);
              }
            }}
            selectedPresetId={selectedAnimation?.preset}
            disabled={selectedElements.length === 0}
            topSlot={appliedSlot}
          />
        </section>
      )}
    </div>
  );
};
